import React, { useState, useEffect } from 'react';
import { getCoBrandings, createCoBranding, updateCoBranding, deleteCoBranding, bulkDeleteCoBranding } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { canEdit } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  MoreVertical, 
  Pencil, 
  Trash2,
  Loader2,
  Tags,
  Building,
  CheckSquare,
  Square
} from 'lucide-react';

export default function CoBranding() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Selection for bulk actions
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: ''
  });
  const [saving, setSaving] = useState(false);
  
  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      const response = await getCoBrandings({ is_active: true });
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load co-branding partners');
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => 
    !search || 
    item.name?.toLowerCase().includes(search.toLowerCase()) ||
    item.description?.toLowerCase().includes(search.toLowerCase())
  );

  // Selection handlers
  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(i => i.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectMode(false);
  };

  // Bulk delete
  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    setDeleting(true);
    try {
      // Use bulk delete endpoint for efficiency
      await bulkDeleteCoBranding(Array.from(selectedIds));
      toast.success(`Deleted ${selectedIds.size} co-branding partner(s)`);
      setBulkDeleteOpen(false);
      clearSelection();
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete some partners');
    } finally {
      setDeleting(false);
    }
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      logo_url: ''
    });
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      logo_url: item.logo_url || ''
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingItem) {
        await updateCoBranding(editingItem.id, formData);
        toast.success('Co-branding partner updated');
      } else {
        await createCoBranding(formData);
        toast.success('Co-branding partner created');
      }
      setDialogOpen(false);
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save co-branding partner');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      await deleteCoBranding(itemToDelete.id);
      toast.success('Co-branding partner deleted');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      loadItems();
    } catch (error) {
      toast.error('Failed to delete co-branding partner');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cobranding-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Tags className="w-5 h-5" />
            Co-Branding Partners
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage co-branding and private label partners for orders
          </p>
        </div>
        <div className="flex gap-2">
          {canEdit(user?.role) && (
            <>
              {selectMode ? (
                <>
                  <Button variant="outline" onClick={clearSelection}>
                    Cancel
                  </Button>
                  {selectedIds.size > 0 && (
                    <Button variant="destructive" onClick={() => setBulkDeleteOpen(true)}>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete ({selectedIds.size})
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setSelectMode(true)} data-testid="btn-select-mode">
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Select
                  </Button>
                  <Button onClick={openCreateDialog} data-testid="btn-add-cobranding">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Partner
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Search & Selection Controls */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            {selectMode && (
              <Button variant="ghost" size="sm" onClick={selectAll} className="shrink-0">
                {selectedIds.size === filteredItems.length ? (
                  <CheckSquare className="w-4 h-4 mr-2" />
                ) : (
                  <Square className="w-4 h-4 mr-2" />
                )}
                {selectedIds.size === filteredItems.length ? 'Deselect All' : 'Select All'}
              </Button>
            )}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search co-branding partners..."
                className="pl-10"
                data-testid="input-search-cobranding"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <div className="text-sm text-muted-foreground">
        {selectMode && selectedIds.size > 0 ? (
          <span className="text-primary font-medium">{selectedIds.size} selected • </span>
        ) : null}
        Showing {filteredItems.length} of {items.length} partners
      </div>

      {/* Partners Grid */}
      {filteredItems.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredItems.map((item) => (
            <Card 
              key={item.id} 
              className={`hover:shadow-md transition-shadow cursor-pointer ${
                selectedIds.has(item.id) ? 'ring-2 ring-primary bg-primary/5' : ''
              }`}
              onClick={() => selectMode && toggleSelect(item.id)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {selectMode && (
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={() => toggleSelect(item.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Building className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{item.name}</CardTitle>
                    </div>
                  </div>
                  {canEdit(user?.role) && !selectMode && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={(e) => e.stopPropagation()}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(item)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {item.description || 'No description'}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Tags className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-medium mb-2">No co-branding partners</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {search ? 'Try adjusting your search' : 'Add your first co-branding partner to get started'}
            </p>
            {!search && canEdit(user?.role) && (
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Partner
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Edit Co-Branding Partner' : 'Add Co-Branding Partner'}
            </DialogTitle>
            <DialogDescription>
              Co-branding partners can be assigned to orders for reporting and tracking.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Aldi Ireland, Dunnes Stores"
                data-testid="input-cobranding-name"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description or notes about this partner..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Logo URL (optional)</Label>
              <Input
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://example.com/logo.png"
              />
              <p className="text-xs text-muted-foreground">
                URL to partner's logo for labels (future feature)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} data-testid="btn-save-cobranding">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingItem ? 'Save Changes' : 'Add Partner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Co-Branding Partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{itemToDelete?.name}" from your co-branding partners. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} Co-Branding Partners?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the selected co-branding partners. 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-red-600 hover:bg-red-700"
              disabled={deleting}
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete {selectedIds.size} Partners
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
